import React, { useEffect, useState } from "react";
import {
  setCurrentSubcategory,
  setMaxMarks,
  addSubQuestionData,
  clearSubQuestionData,
} from "../../../../../redux/reducers/question_reducer";
import { fetchSubcategories } from "../../../../../redux/slices/subcategoriesListSlice";
import { useDispatch, useSelector } from "react-redux";
import { useFetchQuestionsForMock } from "../../../../../hooks/FetchQuestion";

import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import "./MockTestQuestions.css";

import { getQuestionImageLinkForMock } from "../../../../../api/auth";
import ScrollDown from "./ScrollDown";

function getChildParts(questionObj) {
  const raw = questionObj?.child_question_ids;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return typeof raw[0] === "object" ? raw : [];
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && typeof parsed[0] === "object" ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export default function MockTestQuestions({
  savedAnswer,
  onAnswerChange,
}) {
  const dispatch = useDispatch();
  const [textboxValue, setTextboxValue] = useState("");
  const [image, setImage] = useState(null);
  const university_name = useSelector((state) => state.auth.universityName);
  const course = useSelector((state) => state.auth.courseName);
  const mock_name = useSelector((state) => state.questions.mock_name);

  const [checkedAnswers, setCheckedAnswers] = useState({});
  const { trace } = useSelector((state) => state.questions);

  const [{ isLoading, serverError, metadata }] = useFetchQuestionsForMock(
    course,
    university_name,
    mock_name
  );

  const questionObj = useSelector(
    (state) => state.questions.queue[state.questions.trace]
  );

  const [childTextboxValues, setChildTextboxValues] = useState({});
  const [showChildQuestions, setShowChildQuestions] = useState(false);
  const [currentChildIndex, setCurrentChildIndex] = useState(0);

  useEffect(() => {
    if (questionObj?.queue?.length > 0) {
      dispatch(setCurrentSubcategory(questionObj.queue[0]?.subcategory));
    } else if (questionObj?.subcategory) {
      dispatch(setCurrentSubcategory(questionObj.subcategory));
    }
  }, [questionObj, dispatch]);

  useEffect(() => {
    if (metadata) {
      dispatch(fetchSubcategories(metadata));
    }
  }, [metadata, dispatch]);

  useEffect(() => {
    const fetchImage = async () => {
      if (questionObj?.id) {
        try {
          const imageLink = await getQuestionImageLinkForMock(questionObj.id);
          if (imageLink?.data?.session?.[0]?.image) {
            setImage(imageLink.data.session[0].image);
          } else {
            setImage(null);
          }
        } catch {
          setImage(null);
        }
      } else {
        setImage(null);
      }
    };
    fetchImage();
  }, [questionObj]);

  useEffect(() => {
    if (questionObj?.max_marks) {
      dispatch(setMaxMarks(questionObj.max_marks));
    }
  }, [questionObj, dispatch]);

  useEffect(() => {
    if (!questionObj) return;

    if (!savedAnswer) {
      setTextboxValue("");
      setChildTextboxValues({});
      setShowChildQuestions(false);
      setCurrentChildIndex(0);
      return;
    }

    if (savedAnswer.type === 1) {
      const optionIndex = questionObj.options.findIndex(
        (opt) => opt.option_id === savedAnswer.option_id
      );
      if (optionIndex >= 0) {
        setCheckedAnswers((prev) => ({
          ...prev,
          [trace]: optionIndex,
        }));
      }
    }

    if (savedAnswer.type === 2) {
      setTextboxValue(savedAnswer.text || "");
    }

    if (savedAnswer.type === 3) {
      setTextboxValue(savedAnswer.parent_text || "");
      setChildTextboxValues(savedAnswer.child_answers || {});
      setShowChildQuestions(Boolean(savedAnswer.show_child_questions));
      setCurrentChildIndex(savedAnswer.current_child_index ?? 0);
      if (savedAnswer.subquestion_data?.length) {
        dispatch(clearSubQuestionData());
        savedAnswer.subquestion_data.forEach((item) =>
          dispatch(addSubQuestionData(item))
        );
      }
    }
  }, [questionObj?.id, savedAnswer, trace, dispatch]);

  const emitAnswer = (payload) => {
    if (questionObj && onAnswerChange) {
      onAnswerChange(questionObj.id, { ...payload, type: questionObj.type });
    }
  };

  function onSelect(optionId, index) {
    setCheckedAnswers((prev) => ({ ...prev, [trace]: index }));
    emitAnswer({ option_id: optionId, option_index: index });
  }

  const handleTextChange = (value) => {
    setTextboxValue(value);
    if (questionObj?.type === 2) {
      emitAnswer({ text: value });
    } else if (questionObj?.type === 3) {
      emitAnswer({
        parent_text: value,
        child_answers: childTextboxValues,
        show_child_questions: showChildQuestions,
        current_child_index: currentChildIndex,
      });
    }
  };

  const handleChildTextChange = (index, value) => {
    const next = { ...childTextboxValues, [index]: value };
    setChildTextboxValues(next);
    emitAnswer({
      parent_text: textboxValue,
      child_answers: next,
      show_child_questions: showChildQuestions,
      current_child_index: currentChildIndex,
    });
  };

  if (isLoading) return <h3 className="text-light">Loading...</h3>;
  if (serverError)
    return <h3 className="text-light">{serverError || "Unknown Error"}</h3>;
  if (!questionObj) return <div>Loading...</div>;

  const childParts = getChildParts(questionObj);

  return (
    <div className="questions">
      {questionObj.type === 1 && (
        <>
          <h2 className="question-text">{questionObj?.question}</h2>
          <div className="warning-message">
            Your choice is saved when you go to the next question. You can go
            back and change it until you finish the test.
          </div>
          <ScrollDown />
          <div>
            {image ? (
              <img
                src={image}
                alt=""
                style={{ maxWidth: "100%", height: "auto" }}
              />
            ) : null}
          </div>
          <FormGroup role="radiogroup">
            {questionObj?.options
              .filter((option) => option.option_id !== undefined)
              .map((option, i) => (
                <div key={option.option_id} className="option-border-style">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                      backgroundColor:
                        checkedAnswers[trace] === i ? "#d3d3d3" : "transparent",
                      borderRadius: "4px",
                      padding: "8px",
                    }}
                  >
                    <FormControlLabel
                      value={option.option_id}
                      control={
                        <Radio
                          checked={checkedAnswers[trace] === i}
                          onChange={() => onSelect(option.option_id, i)}
                          name="options"
                          color="primary"
                        />
                      }
                      label={option.option_text}
                      className="question-option-label"
                    />
                  </div>
                </div>
              ))}
          </FormGroup>
        </>
      )}

      {questionObj.type === 2 && (
        <div>
          <h2 className="question-text">{questionObj?.question}</h2>
          {image ? (
            <img
              src={image}
              alt=" "
              style={{ maxWidth: "100%", height: "auto" }}
            />
          ) : null}
          <input
            type="text"
            placeholder="Enter your answer"
            className="fancy-text-input"
            value={textboxValue}
            onChange={(e) => handleTextChange(e.target.value)}
          />
        </div>
      )}

      {questionObj.type === 3 && (
        <div>
          <h2 className="question-text">{questionObj?.question}</h2>
          {image ? (
            <img
              src={image}
              alt=" "
              style={{ maxWidth: "100%", height: "auto" }}
            />
          ) : null}
          <input
            type="text"
            placeholder="Enter your answer to the main question"
            className="fancy-text-input"
            value={textboxValue}
            onChange={(e) => handleTextChange(e.target.value)}
          />
          {!showChildQuestions && childParts.length > 0 && (
            <button
              className="reveal-button"
              type="button"
              onClick={() => {
                setShowChildQuestions(true);
                emitAnswer({
                  parent_text: textboxValue,
                  child_answers: childTextboxValues,
                  show_child_questions: true,
                  current_child_index: 0,
                });
              }}
            >
              Continue to sub-questions
            </button>
          )}
          {showChildQuestions && childParts.length > 0 && (
            <div className="child-questions-container">
              {childParts
                .slice(0, currentChildIndex + 1)
                .map((childQuestion, index) => (
                  <div key={index} className="child-question">
                    <h3 className="child-question-text">
                      {childQuestion.question_b_text ||
                        childQuestion.question_c_text ||
                        childQuestion.question_d_text ||
                        childQuestion.question_e_text ||
                        childQuestion.question_f_text ||
                        childQuestion.question_g_text ||
                        childQuestion.question_h_text ||
                        childQuestion.question_i_text}
                    </h3>
                    <input
                      type="text"
                      placeholder="Enter your answer"
                      className="fancy-text-input"
                      value={childTextboxValues[index] || ""}
                      onChange={(e) =>
                        handleChildTextChange(index, e.target.value)
                      }
                      disabled={index < currentChildIndex}
                    />
                    {index === currentChildIndex &&
                      currentChildIndex < childParts.length - 1 && (
                        <button
                          className="reveal-button"
                          type="button"
                          onClick={() => {
                            const nextIndex = currentChildIndex + 1;
                            setCurrentChildIndex(nextIndex);
                            emitAnswer({
                              parent_text: textboxValue,
                              child_answers: childTextboxValues,
                              show_child_questions: true,
                              current_child_index: nextIndex,
                            });
                          }}
                        >
                          Next sub-question
                        </button>
                      )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
